<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { BRAND_NAMES, brandsOf, findModel, type PartModel } from '../model/parts'
import { CUSTOM_MODEL, isValidCustomWatts, LIMITS, type ModelChoice, NO_GPU } from '../model/spec'
import CountSelect from './CountSelect.vue'
import FormRow from './FormRow.vue'

/**
 * A processor or graphics card: a model from the catalogue, grouped by brand and
 * labelled with its rated draw, or a wattage typed in for anything not listed.
 */
const props = defineProps<{
  modelValue: ModelChoice
  id: string
  label: string
  catalogue: readonly PartModel[]
  maxCount: number
  /** Offers "integrated graphics" — a build with no card at all. */
  allowNone?: boolean
}>()
const emit = defineEmits<{ 'update:modelValue': [ModelChoice] }>()

const { t, n } = useI18n()

const groups = computed(() =>
  brandsOf(props.catalogue).map((brand) => ({
    brand,
    name: BRAND_NAMES[brand],
    models: props.catalogue.filter((model) => model.brand === brand),
  })),
)

const isCustom = computed(() => props.modelValue.model === CUSTOM_MODEL)
const isNone = computed(() => props.modelValue.model === NO_GPU)

// A blank field is NaN, not Number('')'s 0, so clearing it reads as invalid
// rather than as a zero-watt part. v-model on a number input hands back a
// number, or the raw string when the field does not parse — hence both.
const parseWatts = (value: string | number): number =>
  String(value).trim() === '' ? NaN : Number(value)

// The draft keeps a half-typed or cleared field exactly as typed.
const watts = ref<string | number>(String(props.modelValue.customWatts))
watch(
  () => props.modelValue.customWatts,
  (value) => {
    if (!Object.is(parseWatts(watts.value), value)) watts.value = String(value)
  },
)

const wattsInvalid = computed(() => isCustom.value && !isValidCustomWatts(parseWatts(watts.value)))
const errorId = computed(() => (wattsInvalid.value ? `${props.id}-error` : undefined))

function onModel(event: Event): void {
  const target = event.target
  if (!(target instanceof HTMLSelectElement)) return
  const model = target.value
  if (model === CUSTOM_MODEL) {
    // Start from the part just chosen: "like this one, but 30 W more" is the
    // usual reason for going custom.
    const known = findModel(props.catalogue, props.modelValue.model)
    const customWatts = known?.watts ?? props.modelValue.customWatts
    emit('update:modelValue', { ...props.modelValue, model, customWatts })
    return
  }
  emit('update:modelValue', { ...props.modelValue, model })
}

function onWatts(): void {
  emit('update:modelValue', { ...props.modelValue, customWatts: parseWatts(watts.value) })
}
</script>

<template>
  <FormRow :label="label" :for-id="id">
    <template v-if="!isNone">
      <CountSelect
        :id="`${id}-count`"
        :model-value="modelValue.count"
        :min="1"
        :max="maxCount"
        :label="t('form.quantity', { part: label })"
        @update:model-value="(count) => emit('update:modelValue', { ...modelValue, count })"
      />
      <span class="form-row__muted" aria-hidden="true">×</span>
    </template>
    <select :id="id" class="model-picker__select" :value="modelValue.model" @change="onModel">
      <option v-if="allowNone" :value="NO_GPU">{{ t('form.integrated') }}</option>
      <optgroup v-for="group in groups" :key="group.brand" :label="group.name">
        <option v-for="model in group.models" :key="model.id" :value="model.id">
          {{ model.name }} — {{ t('form.watts', { value: n(model.watts) }) }}
        </option>
      </optgroup>
      <option :value="CUSTOM_MODEL">{{ t('form.custom') }}</option>
    </select>
    <template v-if="isCustom">
      <label class="visually-hidden" :for="`${id}-watts`">
        {{ t('form.customWatts', { part: label }) }}
      </label>
      <input
        :id="`${id}-watts`"
        v-model="watts"
        class="model-picker__watts mono"
        type="number"
        min="1"
        :max="LIMITS.customWatts"
        step="1"
        inputmode="numeric"
        autocomplete="off"
        :aria-invalid="wattsInvalid"
        :aria-describedby="errorId"
        @input="onWatts"
      />
      <span class="form-row__muted">{{ t('form.wattsUnit') }}</span>
    </template>
    <template #after>
      <p v-if="wattsInvalid" :id="errorId" class="form-row__error" role="alert">
        {{ t('form.customWattsInvalid', { max: n(LIMITS.customWatts) }) }}
      </p>
    </template>
  </FormRow>
</template>

<style scoped>
.model-picker__select {
  flex: 1 1 14rem;
}

.model-picker__watts {
  width: 6rem;
}
</style>
